<script setup>
/**
 * FormDisplayOrderMenu
 * Fully compatible with Vue 2.7 (Composition API) and Vue 3.
 */
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue'
import Button from "../Button.vue"

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

// In Vue 3, you'll eventually replace this with useStore()
const { proxy } = getCurrentInstance()
const store = proxy.$store

// --- State ---

const options = ref([])
const isLoading = ref(false)

// --- Computed ---

const currentData = computed(() => store.state[props.formId]?.currentData)
const tableName = computed(() => store.state[props.formId]?.tableName)
const id = computed(() => store.state[props.formId]?.id)
const showDisplayOrderOption = computed(() => store.getters[`${props.formId}/showDisplayOrderOption`])

// --- Methods ---

/**
 * Replaces the 'asyncComputed' logic.
 * Handles the API call and updates the local 'options' ref.
 */
const fetchOptions = async () => {
    if (!tableName.value) return

    isLoading.value = true
    try {
        const response = await store.dispatch('request/get', {
            url: 'api/items-display-order-options',
            params: {
                tableName: tableName.value,
                id: id.value
            }
        })
        options.value = response.data || []
    } catch (error) {
        console.error("Failed to fetch display orders:", error)
        options.value = []
    } finally {
        isLoading.value = false
    }
}

const setDisplayOrder = (i) => {
    if (currentData.value) {
        currentData.value.displayOrder = i
    }
}

// --- Lifecycle & Watchers ---

onMounted(() => {
    fetchOptions()
})

/**
 * Watch both tableName and id. If the form switches context
 * (e.g., navigating between records), the list refreshes.
 */
watch([tableName, id], () => {
    fetchOptions()
})
</script>

<template>
    <label v-if="showDisplayOrderOption" class="d-flex align-items-center">
        <div class="dropdown me-2">
            <Button
                id="dropdownMenuDisplayOrder"
                aria-expanded="true"
                class="btn-sm dropdown-toggle border"
                data-bs-toggle="dropdown"
                :disabled="isLoading"
            >
                {{ currentData?.displayOrder }}
            </Button>

            <ul aria-labelledby="dropdownMenuDisplayOrder" class="dropdown-menu">
                <li v-for="(i, k) in options" :key="k">
                    <button
                        :class="{ 'active': i === currentData?.displayOrder }"
                        class="dropdown-item"
                        type="button"
                        @click="setDisplayOrder(i)"
                    >
                        {{ i }}
                    </button>
                </li>
            </ul>
        </div>

        Display order
    </label>
</template>

<style scoped>
.dropdown-menu {
    max-height: 300px;
    overflow-y: auto;
}
</style>